import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import bgImage from "./bg.mp4";

const UserProfile = ({ mode }) => {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { token, uid, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `https://sirius.runasp.net/api/Account/GetById?UserId=${uid}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch user");
        }

        const data = await response.json();
        setUser(data);
        setNewUserName(data.userName);
      } catch (err) {
        setError("Failed to fetch user");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [uid, token]);

  const handleClose = () => {
    setOpen(false);
    setNewUserName(user?.userName || "");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const handleChangeUserName = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const response = await fetch(
        "https://sirius.runasp.net/api/Account/ChangeUserName",
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userId: uid,
            userName: newUserName,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to change username");
      }

      setUser((prevUser) => ({ ...prevUser, userName: newUserName }));
      handleClose();
      logout();
      navigate("/admin");
    } catch (err) {
      setError(err.message || "Failed to change username");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!user || newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        "https://sirius.runasp.net/api/Account/ChangePassword",
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            UserId: uid,
            Password: newPassword,
            ConfirmPassword: confirmPassword,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to change password");
      }

      handleClose();
      logout();
      navigate("/admin");
    } catch (err) {
      setError(err.message || "Failed to change password");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <video
          autoPlay
          loop
          muted
          style={{
            width: "400px", // Fixed width
            height: "300px", // Fixed height (optional)
            objectFit: "cover",
            borderRadius: "20px",
            zIndex: -1,
          }}
        >
          <source src={bgImage} type="video/mp4" />
        </video>
      </Box>

      {/* Your profile content */}
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          mt: 10,
          textAlign: "center",
          borderRadius: "10px",
          color: "#fff",
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 20px)", // Ensure the container height adjusts
          margin: "0 auto",
        }}
      >
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
        {loading ? (
          <CircularProgress sx={{ color: "#fff" }} />
        ) : user ? (
          <Box>
            <Typography variant="h6" sx={{ mt: "10px", fontWeight: "medium" }}>
              My Profile
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: "medium" }}>
              Username: {user.userName}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Email: {user.email}
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                bgcolor: "#fff",
                color: "rgb(232, 69, 69)",
                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.8)" },
              }}
              onClick={() => setOpen(true)}
            >
              Edit Profile
            </Button>
          </Box>
        ) : (
          <Typography>Loading...</Typography>
        )}

        <Dialog
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: { p: 2, borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: "bold", color: "rgb(232, 69, 69)" }}>
            Edit User
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="New Username"
              fullWidth
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="New Password"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="Confirm Password"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} sx={{ color: "rgb(232, 69, 69)" }}>
              Cancel
            </Button>
            <Button
              onClick={handleChangeUserName}
              sx={{ color: "rgb(232, 69, 69)" }}
            >
              Change Username
            </Button>
            <Button
              onClick={handleChangePassword}
              sx={{ color: "rgb(232, 69, 69)" }}
            >
              Change Password
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default UserProfile;
